import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Archive, BarChart3, ChevronDown, ClipboardList, FileText, LayoutDashboard,
  LogOut, Menu, Package, Receipt, ScrollText, Settings, ShieldCheck,
  ShoppingCart, Truck, User, Users, X,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import "@/App.css";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import UserManagementModule from './components/UserManagementModule';
import AuditLogModule from './components/AuditLogModule';
import { NavItem, SubNavItem, Toast } from './components/shared/UI';
import ErrorBoundary from './components/shared/ErrorBoundary';
import AppAlert, { installAppAlertOverride } from './components/shared/AppAlert';
import AppConfirm, { showAppConfirm } from './components/shared/AppConfirm';
import { installBeforeUnloadGuard, confirmNavigation } from './hooks/useDirtyForm';
import DashboardModule from './modules/DashboardModule';
import CustomerModule from './modules/CustomerModule';
import ProductModule from './modules/ProductModule';
import SupplierModule from './modules/SupplierModule';
import PurchaseOrderModule from './modules/PurchaseOrderModule';
import InventoryModule from './modules/InventoryModule';
import ReportModule from './modules/ReportModule';
import SalesModule from './modules/SalesModule';
import SettingsModule from './modules/SettingsModule';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

installBeforeUnloadGuard();
installAppAlertOverride();

// Configure axios to send credentials
axios.defaults.withCredentials = true;

// ============== MAIN APP ==============

function MainApp() {
  const { user, logout, isAdmin, isSuperAdmin } = useAuth();
  const [activeModule, setActiveModule] = useState('dashboard');
  // When the user clicks a transaction on the Dashboard we want to jump
  // straight to that document's detail preview, not just to the list view.
  const [pendingDetailTxId, setPendingDetailTxId] = useState(null);
  const [salesMenuOpen, setSalesMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // Default closed on mobile
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  // Data states
  const [stats, setStats] = useState({});
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Toast state
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const roleLabels = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    user: 'User'
  };

  const roleColors = {
    super_admin: 'bg-purple-100 text-purple-700',
    admin: 'bg-blue-100 text-blue-700',
    user: 'bg-slate-100 text-slate-700'
  };

  // Helper to navigate and close sidebar on mobile
  const navigateTo = async (module) => {
    if (!(await confirmNavigation())) return;
    setActiveModule(module);
    // Close sidebar on mobile (window width < 1024px)
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  // Data fetching
  const fetchData = useCallback(async () => {
    try {
      const [statsRes, customersRes, productsRes, categoriesRes, transactionsRes, poRes, suppliersRes, settingsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/transactions`),
        axios.get(`${API}/purchase-orders`),
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/settings`)
      ]);
      
      setStats(statsRes.data);
      setCustomers(customersRes.data);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setTransactions(transactionsRes.data);
      setPurchaseOrders(poRes.data);
      setSuppliers(suppliersRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Update document title and favicon based on settings
  useEffect(() => {
    if (settings?.companyName || settings?.appName) {
      document.title = `${settings.companyName || 'InvoicePro'} | ${settings.appName || 'Invoice App'}`;
    }

    // Prefer explicit favicon, fall back to company logo, else leave the default.
    const faviconSrc = settings?.faviconImage || settings?.logoImage;
    if (faviconSrc) {
      let link = document.querySelector("link[rel='icon']") || document.querySelector("link[rel='shortcut icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      // Detect type so SVG/PNG/ICO are all served correctly.
      const m = /^data:(image\/[a-zA-Z+.-]+);/.exec(faviconSrc);
      link.type = m ? m[1] : 'image/x-icon';
      link.href = faviconSrc;
    }
  }, [settings]);

  useEffect(() => {
    // Fetch data on mount. Seeding tidak lagi dipanggil otomatis di sini —
    // data awal (produk/customer contoh) hanya dibuat sekali via endpoint /seed
    // yang bisa dijalankan manual saat fresh install, dan tidak akan re-run
    // walaupun user menghapus semua data (dikontrol via flag `seededOnce` di DB).
    fetchData();
    // Remove emergent badge
    const removeBadge = () => { const el = document.getElementById('emergent-badge'); if (el) el.remove(); };
    removeBadge();
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [fetchData]);

  // CRUD operations
  const handleAddCustomer = async (data) => {
    try {
      await axios.post(`${API}/customers`, data);
      fetchData();
      showToast('Pelanggan berhasil ditambahkan', 'success');
    } catch (error) {
      showToast('Gagal menambahkan pelanggan', 'error');
    }
  };

  const handleEditCustomer = async (data) => {
    try {
      await axios.put(`${API}/customers/${data.id}`, data);
      fetchData();
      showToast('Pelanggan berhasil diperbarui', 'success');
    } catch (error) {
      showToast('Gagal memperbarui pelanggan', 'error');
    }
  };

  const handleDeleteCustomer = async (id) => {
    if (!(await showAppConfirm('Yakin ingin menghapus pelanggan ini?'))) return;
    try {
      await axios.delete(`${API}/customers/${id}`);
      fetchData();
      showToast('Pelanggan berhasil dihapus', 'success');
    } catch (error) {
      showToast('Gagal menghapus pelanggan', 'error');
    }
  };

  // Supplier CRUD
  const handleAddSupplier = async (data) => {
    try {
      await axios.post(`${API}/suppliers`, data);
      fetchData();
      showToast('Supplier berhasil ditambahkan', 'success');
    } catch (error) {
      showToast('Gagal menambahkan supplier', 'error');
    }
  };

  const handleEditSupplier = async (data) => {
    try {
      await axios.put(`${API}/suppliers/${data.id}`, data);
      fetchData();
      showToast('Supplier berhasil diperbarui', 'success');
    } catch (error) {
      showToast('Gagal memperbarui supplier', 'error');
    }
  };

  const handleDeleteSupplier = async (id) => {
    if (!(await showAppConfirm('Yakin ingin menghapus supplier ini?'))) return;
    try {
      await axios.delete(`${API}/suppliers/${id}`);
      fetchData();
      showToast('Supplier berhasil dihapus', 'success');
    } catch (error) {
      showToast('Gagal menghapus supplier', 'error');
    }
  };

  const handleAddProduct = async (data) => {
    try {
      await axios.post(`${API}/products`, data);
      fetchData();
      showToast('Produk berhasil ditambahkan', 'success');
    } catch (error) {
      showToast('Gagal menambahkan produk', 'error');
    }
  };

  const handleEditProduct = async (data) => {
    try {
      await axios.put(`${API}/products/${data.id}`, data);
      fetchData();
      showToast('Produk berhasil diperbarui', 'success');
    } catch (error) {
      showToast('Gagal memperbarui produk', 'error');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!(await showAppConfirm('Yakin ingin menghapus produk ini?'))) return;
    try {
      await axios.delete(`${API}/products/${id}`);
      fetchData();
      showToast('Produk berhasil dihapus', 'success');
    } catch (error) {
      showToast('Gagal menghapus produk', 'error');
    }
  };

  const handleUpdateStock = async (productId, adjustment) => {
    try {
      await axios.patch(`${API}/products/${productId}/stock`, { adjustment });
      fetchData();
      showToast('Stok berhasil diperbarui', 'success');
    } catch (error) {
      showToast(error.response?.data?.detail || 'Gagal memperbarui stok', 'error');
    }
  };

  const handleAddCategory = async (name) => {
    try {
      await axios.post(`${API}/categories`, { name });
      fetchData();
      showToast('Kategori berhasil ditambahkan', 'success');
    } catch (error) {
      showToast('Gagal menambahkan kategori', 'error');
    }
  };

  const handleAddTransaction = async (data) => {
    try {
      await axios.post(`${API}/transactions`, data);
      fetchData();
      showToast('Transaksi berhasil dibuat', 'success');
    } catch (error) {
      showToast('Gagal membuat transaksi', 'error');
    }
  };

  const handleUpdateTransaction = async (id, data) => {
    try {
      await axios.put(`${API}/transactions/${id}`, data);
      fetchData();
      showToast('Transaksi berhasil diperbarui', 'success');
    } catch (error) {
      showToast(error.response?.data?.detail || 'Gagal memperbarui transaksi', 'error');
    }
  };

  const handleUpdateTransactionStatus = async (id, status) => {
    try {
      await axios.put(`${API}/transactions/${id}/status?status=${status}`);
      fetchData();
      showToast('Status berhasil diperbarui', 'success');
    } catch (error) {
      showToast('Gagal memperbarui status', 'error');
    }
  };

  const handleDeleteTransaction = async (id) => {
    try {
      await axios.delete(`${API}/transactions/${id}`);
      fetchData();
      showToast('Transaksi berhasil dihapus', 'success');
    } catch (error) {
      showToast('Gagal menghapus transaksi', 'error');
    }
  };

  const handleSaveSettings = async (data) => {
    try {
      await axios.put(`${API}/settings`, data);
      fetchData();
      showToast('Pengaturan berhasil disimpan', 'success');
    } catch (error) {
      showToast('Gagal menyimpan pengaturan', 'error');
    }
  };

  const handleSendEmail = async (transactionId, email, subject, message, attachmentBase64, attachmentFilename) => {
    try {
      const res = await axios.post(`${API}/email/send`, {
        transactionId,
        recipientEmail: email,
        subject,
        message,
        attachmentBase64: attachmentBase64 || null,
        attachmentFilename: attachmentFilename || null,
      });
      if (res.data.success) {
        showToast('Email berhasil dikirim!', 'success');
        fetchData();
      } else {
        showToast(res.data.message || 'Gagal mengirim email', 'error');
      }
    } catch (error) {
      showToast(error.response?.data?.detail || 'Gagal mengirim email', 'error');
    }
  };

  const handleSendReminder = async (transactionId, recipientEmail) => {
    try {
      const res = await axios.post(`${API}/email/reminder`, {
        transactionId,
        recipientEmail: recipientEmail || null,
      });
      if (res.data.success) {
        showToast('Payment reminder berhasil dikirim!', 'success');
      } else {
        showToast(res.data.message || 'Gagal mengirim reminder', 'error');
      }
    } catch (error) {
      showToast(error.response?.data?.detail || 'Gagal mengirim reminder', 'error');
    }
  };

  // Purchase Order handlers
  const handleAddPO = async (data) => {
    try {
      await axios.post(`${API}/purchase-orders`, data);
      fetchData();
      showToast('Purchase Order berhasil dibuat', 'success');
    } catch (error) {
      showToast('Gagal membuat Purchase Order', 'error');
    }
  };

  const handleUpdatePO = async (id, data) => {
    try {
      await axios.put(`${API}/purchase-orders/${id}`, data);
      fetchData();
      showToast('Purchase Order berhasil diperbarui', 'success');
    } catch (error) {
      showToast(error.response?.data?.detail || 'Gagal memperbarui PO', 'error');
    }
  };

  const handleReceivePO = async (id) => {
    try {
      await axios.patch(`${API}/purchase-orders/${id}/receive`);
      fetchData();
      showToast('Barang diterima, stok produk telah diperbarui', 'success');
    } catch (error) {
      showToast(error.response?.data?.detail || 'Gagal menerima barang', 'error');
    }
  };

  const handleUpdatePOStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/purchase-orders/${id}/status`, { status });
      fetchData();
      showToast('Status PO berhasil diperbarui', 'success');
    } catch (error) {
      showToast(error.response?.data?.detail || 'Gagal memperbarui status', 'error');
    }
  };

  const handleDeletePO = async (id) => {
    if (!(await showAppConfirm('Yakin ingin menghapus Purchase Order ini?'))) return;
    try {
      await axios.delete(`${API}/purchase-orders/${id}`);
      fetchData();
      showToast('Purchase Order berhasil dihapus', 'success');
    } catch (error) {
      showToast(error.response?.data?.detail || 'Gagal menghapus PO', 'error');
    }
  };

  const userReadOnly = !isAdmin(); // user role = read only

  const renderModule = () => {
    switch (activeModule) {
      case 'dashboard':
        return <DashboardModule stats={stats} loading={loading} onViewTransaction={(t) => { setPendingDetailTxId(t.id); setActiveModule(t.type); }} />;
      case 'customers':
        return <CustomerModule customers={customers} onAdd={handleAddCustomer} onEdit={handleEditCustomer} onDelete={handleDeleteCustomer} loading={loading} readOnly={userReadOnly} />;
      case 'products':
        return <ProductModule products={products} categories={categories} onAdd={handleAddProduct} onEdit={handleEditProduct} onDelete={handleDeleteProduct} onAddCategory={handleAddCategory} loading={loading} readOnly={userReadOnly} />;
      case 'suppliers':
        return <SupplierModule suppliers={suppliers} onAdd={handleAddSupplier} onEdit={handleEditSupplier} onDelete={handleDeleteSupplier} loading={loading} readOnly={userReadOnly} />;
      case 'invoice':
      case 'quote':
      case 'sj':
        return <SalesModule 
          key={activeModule}
          transactions={transactions} 
          products={products} 
          customers={customers} 
          onAdd={handleAddTransaction} 
          onUpdate={handleUpdateTransaction}
          onUpdateStatus={handleUpdateTransactionStatus}
          onDelete={handleDeleteTransaction}
          settings={settings} 
          type={activeModule}
          openDetailTxId={pendingDetailTxId}
          onDetailOpened={() => setPendingDetailTxId(null)}
          onRefresh={fetchData}
          onSendEmail={handleSendEmail}
          onSendReminder={handleSendReminder}
        />;
      case 'inventory':
        return <InventoryModule products={products} loading={loading} onUpdateStock={handleUpdateStock} readOnly={userReadOnly} />;
      case 'po':
        return <PurchaseOrderModule 
          purchaseOrders={purchaseOrders} 
          products={products}
          suppliers={suppliers}
          settings={settings}
          onAdd={handleAddPO} 
          onUpdate={handleUpdatePO}
          onReceive={handleReceivePO}
          onUpdateStatus={handleUpdatePOStatus}
          onDelete={handleDeletePO} 
          loading={loading}
          readOnly={userReadOnly}
        />;
      case 'report':
        return <ReportModule customers={customers} products={products} transactions={transactions} purchaseOrders={purchaseOrders} suppliers={suppliers} settings={settings} />;
      case 'settings':
        if (!isSuperAdmin()) return <DashboardModule stats={stats} loading={loading} />;
        return <SettingsModule 
          settings={settings} 
          onSave={handleSaveSettings}
        />;
      case 'users':
        return <UserManagementModule showToast={showToast} />;
      case 'audit-log':
        return <AuditLogModule />;
      default:
        return <DashboardModule stats={stats} loading={loading} />;
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 w-72 sm:w-64 bg-white border-r border-border/60 flex flex-col transition-transform duration-300 ease-in-out fixed lg:relative h-screen z-40`}>
        <div className="p-5 lg:p-6 border-b border-border/60">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              {settings?.logoImage ? (
                <img 
                  src={settings.logoImage} 
                  alt={settings.companyName || 'Logo'} 
                  className="w-10 h-10 object-contain rounded-xl flex-shrink-0"
                />
              ) : (
                <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileText className="text-primary-foreground" size={20} />
                </div>
              )}
              <div className="min-w-0">
                <h1 className="font-heading font-bold text-base lg:text-lg truncate">{settings?.companyName || 'InvoicePro'}</h1>
                <p className="text-xs text-muted-foreground truncate">{settings?.appName || 'InvoicePro'}</p>
              </div>
            </div>
            {/* Close button for mobile */}
            <button 
              onClick={() => setSidebarOpen(false)}
              className="p-2 hover:bg-slate-100 rounded-lg lg:hidden flex-shrink-0"
              data-testid="close-sidebar-btn"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <NavItem 
            icon={<LayoutDashboard size={20}/>} 
            label="Dashboard" 
            active={activeModule === 'dashboard'}
            onClick={() => navigateTo('dashboard')}
            testId="nav-dashboard"
          />
          <NavItem 
            icon={<Users size={20}/>} 
            label="Pelanggan" 
            active={activeModule === 'customers'}
            onClick={() => navigateTo('customers')}
            testId="nav-customers"
          />
          <NavItem 
            icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M1 5h14v11H1z"/><path d="M15 9h4l3 3v4h-7V9z"/><circle cx="5" cy="18" r="2"/><circle cx="18" cy="18" r="2"/><line x1="7" y1="16" x2="16" y2="16"/><line x1="1" y1="16" x2="3" y2="16"/><path d="M6 7.5l2 1L6 9.5"/><path d="M5 7v4h5V7H5z"/><line x1="5" y1="9" x2="7" y2="9"/></svg>} 
            label="Supplier" 
            active={activeModule === 'suppliers'}
            onClick={() => navigateTo('suppliers')}
            testId="nav-suppliers"
          />
          <NavItem 
            icon={<Package size={20}/>} 
            label="Produk" 
            active={activeModule === 'products'}
            onClick={() => navigateTo('products')}
            testId="nav-products"
          />

          <NavItem 
            icon={<ClipboardList size={20}/>} 
            label="Purchase Order" 
            active={activeModule === 'po'}
            onClick={() => navigateTo('po')}
            testId="nav-po"
          />
          
          <NavItem 
            icon={<Archive size={20}/>} 
            label="Inventory" 
            active={activeModule === 'inventory'}
            onClick={() => navigateTo('inventory')}
            testId="nav-inventory"
          />
          
          <div className="py-2">
            <NavItem 
              icon={<ShoppingCart size={20}/>} 
              label="Penjualan" 
              hasSubmenu 
              isOpen={salesMenuOpen}
              onClick={() => setSalesMenuOpen(!salesMenuOpen)}
              testId="nav-sales"
            />
            <AnimatePresence>
              {salesMenuOpen && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <SubNavItem icon={<ClipboardList size={16}/>} label="Quotation" active={activeModule === 'quote'} onClick={() => navigateTo('quote')} testId="nav-quote" />
                  <SubNavItem icon={<Receipt size={16}/>} label="Invoice" active={activeModule === 'invoice'} onClick={() => navigateTo('invoice')} testId="nav-invoice" />
                  <SubNavItem icon={<Truck size={16}/>} label="Surat Jalan" active={activeModule === 'sj'} onClick={() => navigateTo('sj')} testId="nav-sj" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          <NavItem 
            icon={<BarChart3 size={20}/>} 
            label="Laporan" 
            active={activeModule === 'report'}
            onClick={() => navigateTo('report')}
            testId="nav-report"
          />

          {isSuperAdmin() && (
            <NavItem 
              icon={<Settings size={20}/>} 
              label="Pengaturan" 
              active={activeModule === 'settings'}
              onClick={() => navigateTo('settings')}
              testId="nav-settings"
            />
          )}

          {isAdmin() && (
            <NavItem 
              icon={<ShieldCheck size={20}/>} 
              label="Manajemen User" 
              active={activeModule === 'users'}
              onClick={() => navigateTo('users')}
              testId="nav-users"
            />
          )}

          {isSuperAdmin() && (
            <NavItem 
              icon={<ScrollText size={20}/>} 
              label="Audit Log" 
              active={activeModule === 'audit-log'}
              onClick={() => navigateTo('audit-log')}
              testId="nav-audit-log"
            />
          )}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-screen min-w-0 lg:ml-0">
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-md border-b border-border/60 sticky top-0 z-20">
          <div className="flex items-center justify-between px-4 lg:px-6 py-3 lg:py-4">
            {/* Left side - Hamburger & Logo on mobile */}
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setSidebarOpen(true)}
                className="p-2 hover:bg-slate-100 rounded-lg lg:hidden"
                data-testid="open-sidebar-btn"
              >
                <Menu size={22} />
              </button>
              <div className="flex items-center gap-2 lg:hidden">
                {settings?.logoImage ? (
                  <img 
                    src={settings.logoImage} 
                    alt={settings.companyName || 'Logo'} 
                    className="w-8 h-8 object-contain rounded-lg"
                  />
                ) : (
                  <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                    <FileText className="text-primary-foreground" size={16} />
                  </div>
                )}
                <span className="font-heading font-bold">{settings?.companyName || 'InvoicePro'}</span>
              </div>
            </div>
            
            {/* Right side */}
            <div className="flex items-center gap-2 lg:gap-4 ml-auto">
              <span className="text-sm text-muted-foreground hidden sm:block">
                {settings?.companyName || 'InvoicePro'}
              </span>
              
              {/* User Menu */}
              <div className="relative" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowUserMenu(false); }}>
                <button 
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 lg:gap-3 p-1.5 lg:p-2 hover:bg-slate-100 rounded-xl transition-colors"
                  data-testid="user-menu-btn"
                >
                  <div className="w-8 h-8 lg:w-9 lg:h-9 bg-gradient-to-br from-teal-500 to-cyan-700 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="hidden lg:block text-left">
                    <p className="text-sm font-medium text-slate-700">{user?.name || 'User'}</p>
                    <p className={`text-xs px-1.5 py-0.5 rounded ${roleColors[user?.role] || 'bg-slate-100 text-slate-600'}`}>
                      {roleLabels[user?.role] || 'User'}
                    </p>
                  </div>
                  <ChevronDown size={16} className={`hidden sm:block text-slate-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {showUserMenu && (
                  <>
                    {/* Backdrop to close menu */}
                    <div 
                      className="fixed inset-0 z-[100]" 
                      onClick={() => setShowUserMenu(false)}
                    />
                    <div
                      className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-border/60 py-2 z-[101]"
                      data-testid="user-dropdown-menu"
                    >
                      <div className="px-4 py-3 border-b border-border/60">
                        <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                        <p className="text-xs text-slate-500">{user?.email}</p>
                      </div>
                      
                      <div className="py-1">
                        {isSuperAdmin() && (
                          <button
                            onClick={() => { navigateTo('settings'); setShowUserMenu(false); }}
                            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                            data-testid="menu-settings"
                          >
                            <Settings size={16} className="text-slate-400" />
                            Pengaturan
                          </button>
                        )}
                        
                        {isAdmin() && (
                          <button
                            onClick={() => { navigateTo('users'); setShowUserMenu(false); }}
                            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                            data-testid="menu-users"
                          >
                            <ShieldCheck size={16} className="text-slate-400" />
                            Manajemen User
                          </button>
                        )}
                        
                        {isSuperAdmin() && (
                          <button
                            onClick={() => { navigateTo('audit-log'); setShowUserMenu(false); }}
                            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                            data-testid="menu-audit-log"
                          >
                            <ScrollText size={16} className="text-slate-400" />
                            Audit Log
                          </button>
                        )}
                      </div>
                      
                      <div className="border-t border-border/60 pt-1">
                        <button
                          onClick={() => { setShowUserMenu(false); logout(); }}
                          className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors"
                          data-testid="logout-btn"
                        >
                          <LogOut size={16} />
                          Keluar
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          <ErrorBoundary key={activeModule}>{renderModule()}</ErrorBoundary>
        </div>
      </main>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <Toast 
            message={toast.message} 
            type={toast.type} 
            onClose={() => setToast(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// App wrapper with AuthProvider
function App() {
  const { user, loading } = useAuth();

  // Fetch settings publicly to set browser tab title & favicon BEFORE login.
  useEffect(() => {
    const applyBrand = (settings) => {
      if (!settings) return;
      const title = `${settings.companyName || 'InvoicePro'} | ${settings.appName || 'Invoice App'}`;
      document.title = title;
      const faviconSrc = settings.faviconImage || settings.logoImage;
      if (faviconSrc) {
        let link = document.querySelector("link[rel='icon']") || document.querySelector("link[rel='shortcut icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        const m = /^data:(image\/[a-zA-Z+.-]+);/.exec(faviconSrc);
        link.type = m ? m[1] : 'image/x-icon';
        link.href = faviconSrc;
      }
    };
    axios.get(`${API}/settings`).then(res => applyBrand(res.data)).catch(() => {});
  }, []);

  // Handle /verify-email route
  if (window.location.pathname === '/verify-email') {
    return <VerifyEmailPage />;
  }

  // Handle /reset-password route
  if (window.location.pathname === '/reset-password') {
    return <ResetPasswordPage />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: '#060917'}}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 rounded-full animate-spin mx-auto mb-4" style={{borderColor: 'rgba(101,113,255,0.3)', borderTopColor: '#6571FF'}}></div>
          <p style={{color: '#ADB5BD'}}>Memuat...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  return <MainApp />;
}

// Root component with AuthProvider
function AppWithAuth() {
  return (
    <AuthProvider>
      <App />
      <AppAlert />
      <AppConfirm />
    </AuthProvider>
  );
}

export default AppWithAuth;
